import { useState } from "react";
import { motion } from "framer-motion";
import { Check, BookOpen } from "lucide-react";
import PlanExamplesDialog from "@/components/landing/PlanExamples";

const EASE = [0.22, 1, 0.36, 1];

const TIERS = [
  {
    id: "free",
    name: "Free",
    monthly: 0,
    annual: 0,
    badge: "Forever",
    features: [
      "Up to 3 members",
      "1 project",
      "Kanban board & task table",
      "Dashboard & overdue watchlist",
      "My Work across workspaces",
      "Bulk edit with undo",
    ],
    cta: "Start free",
  },
  {
    id: "starter",
    name: "Starter",
    monthly: 8,
    annual: 7,
    badge: "Small teams",
    features: [
      "Everything in Free",
      "Up to 10 members",
      "Unlimited projects",
      "Time Off calendar with holidays",
      "Private Mind map board",
    ],
    cta: "Start free trial",
  },
  {
    id: "team",
    name: "Team",
    monthly: 15,
    annual: 12,
    badge: "Most popular",
    highlight: true,
    features: [
      "Everything in Starter",
      "Up to 50 members",
      "Timeline / Gantt with dependencies",
      "Review workflow & To Review queue",
      "Announcements with pop-ups, banners & seen-by",
      "Discipline groups with group leads",
      "Viewer role (each Viewer counts as a paid seat)",
      "@mentions & task discussions",
      "Printable reports with AI executive summary",
    ],
    cta: "Start free trial",
  },
  {
    id: "business",
    name: "Business",
    monthly: 25,
    annual: 21,
    badge: "For scale",
    features: [
      "Everything in Team",
      "Unlimited members",
      "Unlimited free Viewer seats (read-only)",
      "Admin audit log + CSV export",
      "Data retention controls",
      "Priority support",
    ],
    cta: "Start free trial",
  },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);
  const [openTier, setOpenTier] = useState(null);

  return (
    <section id="pricing" className="relative mx-auto max-w-7xl px-6 py-32" data-testid="pricing-section">
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-[#00FF66]"
      >
        Pricing
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
        className="font-display max-w-xl text-4xl font-black tracking-tighter md:text-5xl"
      >
        Priced below the giants. Built sharper.
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, delay: 0.18, ease: EASE }}
        className="mt-5 max-w-lg text-base leading-relaxed text-white/50"
      >
        Every paid plan starts with a 7-day free trial — no credit card required. Per seat,
        cancel anytime.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.24, ease: EASE }}
        className="mb-14 mt-9 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] p-1"
        data-testid="pricing-interval-toggle"
      >
        {[
          { key: false, label: "Monthly" },
          { key: true, label: "Yearly · save 20%" },
        ].map((opt) => (
          <button
            key={String(opt.key)}
            onClick={() => setYearly(opt.key)}
            data-testid={`pricing-interval-${opt.key ? "yearly" : "monthly"}`}
            className={`rounded-full px-5 py-2 text-xs font-bold transition-colors duration-200 ${
              yearly === opt.key ? "bg-[#00FF66] text-black" : "text-white/50 hover:text-white"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        {TIERS.map((tier, i) => (
          <motion.div
            key={tier.id}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: EASE }}
            whileHover={{ y: -6 }}
            className={`relative flex flex-col overflow-hidden rounded-3xl border p-8 backdrop-blur-md ${
              tier.highlight
                ? "border-[#00FF66]/40 bg-[#00FF66]/[0.06] shadow-[0_0_60px_rgba(0,255,102,0.12)]"
                : "border-white/10 bg-white/[0.04]"
            }`}
            data-testid={`pricing-tier-${tier.id}`}
          >
            {tier.highlight && (
              <span
                className="animate-beam absolute top-0 h-px w-16 bg-gradient-to-r from-transparent via-[#00FF66] to-transparent"
                aria-hidden="true"
              />
            )}
            <div className="mb-8 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-xl font-bold">{tier.name}</h3>
                <button
                  onClick={() => setOpenTier(tier)}
                  title={`See how ${tier.name} works`}
                  aria-label={`See how ${tier.name} works`}
                  className="rounded-full border border-white/12 p-1.5 text-white/40 transition-colors duration-200 hover:border-[#00FF66]/50 hover:text-[#00FF66]"
                  data-testid={`pricing-examples-${tier.id}`}
                >
                  <BookOpen className="h-3.5 w-3.5" />
                </button>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
                  tier.highlight ? "bg-[#00FF66] text-black" : "border border-white/15 text-white/50"
                }`}
              >
                {tier.badge}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <p className="font-display text-5xl font-black tracking-tight" data-testid={`pricing-amount-${tier.id}`}>
                ${yearly ? tier.annual : tier.monthly}
              </p>
              <p className="text-sm text-white/45">{tier.id === "free" ? "forever" : "per seat / month"}</p>
            </div>
            {tier.id !== "free" && (
              <p className="mt-2 text-xs text-white/35">
                {yearly ? `billed $${tier.annual * 12} / seat / year` : "billed monthly"}
              </p>
            )}
            <ul className="mt-8 flex-1 space-y-3">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm text-white/65">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#00FF66]" />
                  {feature}
                </li>
              ))}
            </ul>
            <motion.a
              href={`/signup?plan=${tier.id}`}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className={`mt-10 block w-full rounded-full py-3.5 text-center text-sm font-bold ${
                tier.highlight
                  ? "bg-[#00FF66] text-black shadow-[0_0_30px_rgba(0,255,102,0.35)]"
                  : "border border-white/15 bg-white/5 text-white transition-colors duration-200 hover:border-[#00FF66]/50 hover:text-[#00FF66]"
              }`}
              data-testid={`pricing-cta-${tier.id}`}
            >
              {tier.cta}
            </motion.a>
            <button
              onClick={() => setOpenTier(tier)}
              className="mt-3 flex w-full items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-white/35 transition-colors duration-200 hover:text-[#00FF66]"
              data-testid={`pricing-examples-link-${tier.id}`}
            >
              <BookOpen className="h-3 w-3" />
              See how it works
            </button>
          </motion.div>
        ))}
      </div>

      <PlanExamplesDialog tier={openTier} onClose={() => setOpenTier(null)} />
    </section>
  );
}
